import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AircraftHttpClient } from 'src/app/shared/flight-log-api/aircraft/aircraft-client.service';
import { BaseHttpClient } from 'src/app/shared/flight-log-api/bases/base-client.service';
import { DutyLogHttpClient } from 'src/app/shared/flight-log-api/duty-logs/duty-log-client.service';
import { FboHttpClient } from 'src/app/shared/flight-log-api/fbos/fbo-client.service';
import { FlightLogHttpClient } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.service';
import { FuelTankHttpClient } from 'src/app/shared/flight-log-api/fuel-tanks/fuel-tank-client.service';
import { KpiMetrics } from 'src/app/shared/flight-log-api/kpi/kpi.model';
import { MaintenanceHttpClient } from 'src/app/shared/flight-log-api/maintenance/maintenance-client.service';
import { AppState } from 'src/store';
import { UpsertApproachTypes } from 'src/store/approach-type/approach-type.actions';
import { ApproachTypeNgrx } from 'src/store/approach-type/approach-type.model';
import { DeleteCompany, UpsertCompany } from 'src/store/company/company.actions';
import { CompanyEntity } from 'src/store/company/company.model';
import { UpsertDepartureTypes } from 'src/store/departure-type/departure-type.actions';
import { DepartureTypeNgrx } from 'src/store/departure-type/departure-type.model';
import { assertNotNullOrWhitespace } from 'src/util/values';
import { FlightLogApiConfiguration, FLIGHT_LOG_API_CONFIG } from './flight-log-api-configuration';

@Injectable({ providedIn: 'root' })
export class FlightLogApiHttpClient {
  constructor(
    @Inject(FLIGHT_LOG_API_CONFIG) private readonly _config: FlightLogApiConfiguration,
    public readonly fbos: FboHttpClient,
    public readonly fuelTanks: FuelTankHttpClient,
    public readonly flightLogs: FlightLogHttpClient,
    public readonly bases: BaseHttpClient,
    public readonly maintenance: MaintenanceHttpClient,
    public readonly dutyLogs: DutyLogHttpClient,
    public readonly aircraft: AircraftHttpClient,
    private readonly _store: Store<AppState>,
    private readonly _http: HttpClient,
  ) {}

  public getDepartureTypes(): Observable<DepartureTypeNgrx[]> {
    return this._http.get<DepartureTypeNgrx[]>(this._url('departureTypes')).pipe(
      tap((types) => {
        this._store.dispatch(new UpsertDepartureTypes({ departureTypes: types }));
      }),
    );
  }

  public getApproachTypes(): Observable<ApproachTypeNgrx[]> {
    return this._http.get<ApproachTypeNgrx[]>(this._url('approachTypes')).pipe(
      tap((types) => {
        this._store.dispatch(new UpsertApproachTypes({ approachTypes: types }));
      }),
    );
  }

  public getKpiStats(pilotEmployeeId: string) {
    return this._http.get<KpiMetrics>(this._url('stats/pilot'), {
      params: { pilotId: pilotEmployeeId },
    });
  }

  public getCompany(companyId: string): Observable<CompanyEntity | undefined> {
    assertNotNullOrWhitespace(companyId, 'companyId');
    return this._http
      .get<CompanyEntity | undefined>(`${this._config.baseUri}/api/companies/${companyId}`)
      .pipe(
        tap((company) => {
          this._store.dispatch(
            company != null ? new UpsertCompany({ company }) : new DeleteCompany({ id: companyId }),
          );
        }),
      );
  }

  private _url(path: string) {
    return this._config.baseUri + '/api/' + path;
  }
}
